import { useEffect, useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useForceLightMode } from '@/hooks/useForceLightMode'
import { BillingApiError, BillingService } from '@/services/BillingService'
import { preloadHsnDatabase } from '@/components/pos/hsnPreload'
import {
  ShoppingCart,
  FileText,
  CalendarRange,
  Users,
  Package,
  Box,
  History,
  Settings,
  LogOut,
  Menu,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  ClipboardList,
  Shield,
  Link as LinkIcon,
  ArrowRightLeft,
  Banknote,
  Truck,
} from 'lucide-react'

const navItems = [
  { path: '/pos', label: 'Retail Bill', icon: ShoppingCart },
  { path: '/pos/challan', label: 'Challan', icon: FileText },
  { path: '/pos/billing', label: 'Month-End', icon: CalendarRange },
  { path: '/pos/quotes', label: 'Quotations', icon: ClipboardList },
  { path: '/pos/payment-links', label: 'Pay Links', icon: LinkIcon },
  { path: '/pos/transactions', label: 'Transactions', icon: ArrowRightLeft },
  { path: '/pos/cheque', label: 'Cheques', icon: Banknote },
  { path: '/pos/products', label: 'Products', icon: Box },
  { path: '/pos/customers', label: 'Customers', icon: Users },
  { path: '/pos/inventory', label: 'Inventory', icon: Package },
  { path: '/pos/purchases', label: 'Purchases', icon: Truck },
  { path: '/pos/history', label: 'History', icon: History },
  { path: '/pos/settings', label: 'Settings', icon: Settings },
]

export function PosLayout() {
  useForceLightMode()
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [authed, setAuthed] = useState<boolean | null>(null)

  useEffect(() => {
    BillingService.me()
      .then(() => {
        setAuthed(true)
        preloadHsnDatabase()
      })
      .catch((err) => {
        // Only an actual 401 means "not logged in" — a 429 (global rate
        // limit, unrelated to session validity), a 5xx, or a network
        // failure must NOT force a logout; that would kick out a still-
        // validly-logged-in user just for making too many requests, or for
        // a transient blip. Treat those as "couldn't confirm auth yet"
        // rather than "definitely logged out".
        if (err instanceof BillingApiError && err.status === 401) {
          setAuthed(false)
          navigate('/pos/login', { replace: true })
        } else {
          setAuthed(true)
          preloadHsnDatabase()
        }
      })
  }, [navigate])

  const handleLogout = async () => {
    await BillingService.logout()
    navigate('/pos/login', { replace: true })
  }

  if (authed === null) {
    return (
      <div data-no-dark className="flex h-dvh items-center justify-center bg-gray-50">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    )
  }

  if (!authed) return null

  return (
    <div data-no-dark className="flex h-dvh bg-gray-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-56 bg-white border-r shadow-sm transform transition-all duration-200 lg:relative lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${collapsed ? 'lg:w-16' : 'lg:w-56'}`}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h1 className={`text-lg font-bold text-gray-900 truncate ${collapsed ? 'lg:hidden' : ''}`}>StatioMart POS</h1>
          <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
            <X className="h-5 w-5" />
          </button>
          <button
            className="relative group hidden lg:block p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            <span className="tooltip-side">{collapsed ? 'Expand sidebar' : 'Collapse sidebar'}</span>
          </button>
        </div>
        <nav className="p-2 space-y-1">
          {navItems.map((item) => {
            const active =
              item.path === '/pos'
                ? location.pathname === '/pos'
                : location.pathname.startsWith(item.path)
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`relative group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  collapsed ? 'lg:justify-center' : ''
                } ${
                  active
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <item.icon className="h-4 w-4 shrink-0" />
                <span className={collapsed ? 'lg:hidden' : ''}>{item.label}</span>
                {collapsed && (
                  <span className="hidden lg:block absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                    {item.label}
                  </span>
                )}
              </Link>
            )
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-2 border-t space-y-1">
          <Link
            to="/admin/products"
            onClick={() => setSidebarOpen(false)}
            className={`relative group flex items-center gap-3 px-3 py-2 w-full rounded-lg text-sm font-medium text-purple-600 hover:bg-purple-50 ${
              collapsed ? 'lg:justify-center' : ''
            }`}
          >
            <Shield className="h-4 w-4 shrink-0" />
            <span className={collapsed ? 'lg:hidden' : ''}>Go to Admin</span>
            {collapsed && (
              <span className="hidden lg:block absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                Go to Admin
              </span>
            )}
          </Link>
          <button
            onClick={handleLogout}
            className={`relative group flex items-center gap-3 px-3 py-2 w-full rounded-lg text-sm text-red-600 hover:bg-red-50 ${
              collapsed ? 'lg:justify-center' : ''
            }`}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span className={collapsed ? 'lg:hidden' : ''}>Logout</span>
            {collapsed && (
              <span className="hidden lg:block absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                Logout
              </span>
            )}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-10 flex items-center gap-3 p-3 border-b bg-white lg:hidden">
          <button onClick={() => setSidebarOpen(true)}>
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="text-sm font-semibold">StatioMart POS</h1>
        </header>
        <main className="flex-1 overflow-auto p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
